import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useCreateEventContext } from "../../../context/CreateEventContext";
import stripe_logo from "../../../assets/images/stripe_logo.svg";
import { useAuth } from "../../../context/AuthContext";
import { CheckIcon, ChevronLeftIcon, ClipboardCheckIcon, LightningBoltIcon } from "@heroicons/react/solid";
import { getFunctions, httpsCallable } from "firebase/functions";

const CreateEventContent3 = ({
  handleContinue,
  handlePrevious,
  handleSaveForLater,
}) => {
  const { t } = useTranslation();
  const { eventData, updateEventData } = useCreateEventContext();
  const { user, stripeConnected, subscriptionTier } = useAuth();
  const [transactionFeePercentage, setTransactionFeePercentage] = useState(
    eventData?.transactionFeePercentage || ""
  );
  const [paymentTiming, setPaymentTiming] = useState(
    eventData?.paymentTiming || "after_approval"
  );
  const [feeLevels, setFeeLevels] = useState(
    eventData?.feeLevels || [
      {
        name: "General Booth",
        description: "Standard vendor booth space",
        fee: "",
        isSet: false,
      },
    ]
  );

  useEffect(() => {
    if (eventData) {
      setTransactionFeePercentage(eventData.transactionFeePercentage || "");
      setPaymentTiming(eventData.paymentTiming || "after_approval");
      if (eventData.feeLevels) {
        setFeeLevels(eventData.feeLevels);
      }
    }
  }, [eventData]);

  const handleSetFee = (index) => {
    const updatedFeeLevels = [...feeLevels];
    updatedFeeLevels[index].isSet = true;
    setFeeLevels(updatedFeeLevels);
  };

  const handleFeeChange = (index, value) => {
    const updatedFeeLevels = [...feeLevels];
    updatedFeeLevels[index].fee = value;
    updatedFeeLevels[index].isSet = false;
    setFeeLevels(updatedFeeLevels);
  };

  const handleNameChange = (index, value) => {
    const updatedFeeLevels = [...feeLevels];
    updatedFeeLevels[index].name = value;
    setFeeLevels(updatedFeeLevels);
  };

  const handleDescriptionChange = (index, value) => {
    const updatedFeeLevels = [...feeLevels];
    updatedFeeLevels[index].description = value;
    setFeeLevels(updatedFeeLevels);
  };

  const removeFeeLevel = (index) => {
    const updatedFeeLevels = feeLevels.filter((_, i) => i !== index);
    setFeeLevels(updatedFeeLevels);
  };

  const handleConnectStripe = async () => {
    if (!user) {
      alert("You must be signed in to connect to Stripe.");
      return;
    }

    try {
      const existingAccountId = sessionStorage.getItem('stripeAccountId');

      const functions = getFunctions();
      const createAccount = httpsCallable(functions, 'createStripeConnectAccount');
      const result = await createAccount({
        userId: user?.uid,
        email: user?.email,
        existingAccountId: existingAccountId || undefined,
      });

      const data = result.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to create Stripe account');
      }

      sessionStorage.setItem('stripeAccountId', data.accountId);
      // Save current path so callback can return here after Stripe connect
      sessionStorage.setItem('stripeConnectReturnTo', window.location.pathname);
      window.location.href = data.onboardingUrl;
    } catch (err) {
      console.error('Error connecting to Stripe:', err);
      alert('Failed to connect to Stripe. Please try again.');
    }
  };

  const handleSaveContinue = () => {
    updateEventData({
      transactionFeePercentage,
      feeLevels,
      paymentTiming,
      activeStep: 3,
    });
    handleContinue();
  };

  const handleSaveForLaterClick = () => {
    updateEventData({
      eventId: eventData.eventId,
      activeStep: 2,
      transactionFeePercentage,
      feeLevels,
      paymentTiming,
    });
    handleSaveForLater();
  };

  const handleBack = () => {
    updateEventData({
      transactionFeePercentage,
      feeLevels,
      paymentTiming,
      activeStep: 1,
    });
    handlePrevious();
  };

  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';
  const maxFeeLevels = isPro ? 4 : 1;
  const canAddMoreLevels = feeLevels.length < maxFeeLevels;

  const addNewFeeLevel = () => {
    if (canAddMoreLevels) {
      setFeeLevels([
        ...feeLevels,
        {
          name: "",
          description: "",
          fee: "",
          isSet: false,
        },
      ]);
    }
  };

  const areAllFeesSet = feeLevels.every(
    (level) => level.isSet && level.name && level.description
  );

  return (
    <div className="w-full min-h-full bg-white">
      <div className="max-w-xl mx-auto px-6 py-12">
        
        {/* Back Button - Minimal */}
        <button
          onClick={handleBack}
          className="flex items-center gap-1 text-grey-400 hover:text-grey-600 text-sm mb-12 transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          <span>{t('common.back')}</span>
        </button>

        {/* Header */}
        <div className="mb-16 text-center">
          <h1 className="text-3xl font-semibold text-grey-900 tracking-tight">
            {t('createEvent.pricingPayments')}
          </h1>
          <p className="text-grey-400 mt-3 text-lg">
            {t('createEvent.pricingSubtitle')}
          </p>
        </div>

        {/* Section 1: Approval Method */}
        <div className="mb-16">
          <h2 className="text-xs font-medium text-grey-400 uppercase tracking-wider mb-6">
            {t('createEvent.approvalMethod')}
          </h2>
          
          <div className="space-y-3">
            {/* Review First Option */}
            <button
              onClick={() => setPaymentTiming("after_approval")}
              className={`w-full p-5 rounded-2xl border transition-all text-left flex items-center gap-4 ${
                paymentTiming === "after_approval"
                  ? "border-grey-900 bg-grey-50"
                  : "border-grey-200 hover:border-grey-300 bg-white"
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                paymentTiming === "after_approval"
                  ? "bg-grey-900"
                  : "bg-grey-100"
              }`}>
                <ClipboardCheckIcon className={`h-5 w-5 ${
                  paymentTiming === "after_approval" ? "text-white" : "text-grey-400"
                }`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-grey-900">{t('createEvent.reviewFirstCharge')}</p>
                <p className="text-sm text-grey-400 mt-0.5">
                  {t('createEvent.reviewFirstDesc')}
                </p>
              </div>
              {paymentTiming === "after_approval" && (
                <CheckIcon className="h-5 w-5 text-grey-900 flex-shrink-0" />
              )}
            </button>

            {/* Auto-Approve Option */}
            <button
              onClick={() => setPaymentTiming("upfront")}
              className={`w-full p-5 rounded-2xl border transition-all text-left flex items-center gap-4 ${
                paymentTiming === "upfront"
                  ? "border-grey-900 bg-grey-50"
                  : "border-grey-200 hover:border-grey-300 bg-white"
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                paymentTiming === "upfront"
                  ? "bg-grey-900"
                  : "bg-grey-100"
              }`}>
                <LightningBoltIcon className={`h-5 w-5 ${
                  paymentTiming === "upfront" ? "text-white" : "text-grey-400"
                }`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-grey-900">{t('createEvent.payJoinInstantly')}</p>
                <p className="text-sm text-grey-400 mt-0.5">
                  {t('createEvent.payJoinDesc')}
                </p>
              </div>
              {paymentTiming === "upfront" && (
                <CheckIcon className="h-5 w-5 text-grey-900 flex-shrink-0" />
              )}
            </button>
          </div>
        </div>

        {/* Section 2: Booth Pricing - Super Simple */}
        <div className="mb-16">
          <h2 className="text-xs font-medium text-grey-400 uppercase tracking-wider mb-6">
            {t('createEvent.boothPricing')}
          </h2>
          
          <div className="space-y-4">
            {feeLevels.map((level, index) => (
              <div
                key={index}
                className={`rounded-2xl border overflow-hidden transition-all ${
                  level.isSet 
                    ? "border-grey-300 bg-grey-50" 
                    : "border-grey-200 bg-white"
                }`}
              >
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      value={level.name}
                      onChange={(e) => handleNameChange(index, e.target.value)}
                      placeholder={t('createEvent.boothNamePlaceholder')}
                      className="flex-1 text-lg font-medium text-grey-900 placeholder-grey-300 bg-transparent border-none focus:outline-none focus:ring-0"
                    />
                    <div className="flex items-center gap-1 bg-grey-50 rounded-xl px-4 py-2">
                      <span className="text-grey-400 text-lg">$</span>
                      <input
                        type="number"
                        value={level.fee}
                        onChange={(e) => handleFeeChange(index, e.target.value)}
                        className="w-20 text-lg font-semibold text-grey-900 bg-transparent border-none focus:outline-none focus:ring-0 text-right"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                  <input
                    type="text"
                    value={level.description}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    placeholder={t('createEvent.boothDescPlaceholder')}
                    className="w-full mt-3 text-sm text-grey-500 placeholder-grey-300 bg-transparent border-none focus:outline-none focus:ring-0"
                  />
                </div>
                
                {/* Set/Confirm Row */}
                <div className="px-5 py-3 bg-grey-25 border-t border-grey-100 flex items-center justify-between">
                  {level.isSet ? (
                    <div className="flex items-center gap-2 text-sm text-grey-700 font-medium">
                      <CheckIcon className="h-4 w-4" />
                      <span>Price set</span>
                    </div>
                  ) : (
                    <span className="text-sm text-grey-400">
                      {level.fee && level.name ? t('createEvent.readyToConfirm') : t('createEvent.enterNameAndPrice')}
                    </span>
                  )}
                  
                  <div className="flex items-center gap-2">
                    {index !== 0 && (
                      <button
                        onClick={() => removeFeeLevel(index)}
                        className="text-sm text-grey-400 hover:text-red-500 transition-colors"
                      >
                        {t('createEvent.removeLevel')}
                      </button>
                    )}
                    {!level.isSet && (
                      <button
                        onClick={() => handleSetFee(index)}
                        disabled={!level.fee || !level.name}
                        className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                          level.fee && level.name
                            ? "bg-grey-900 text-white hover:bg-grey-800"
                            : "bg-grey-100 text-grey-300"
                        }`}
                      >
                        {t('createEvent.setPrice')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add More Types (Pro) */}
          {isPro && canAddMoreLevels && (
            <button
              onClick={addNewFeeLevel}
              className="w-full mt-4 p-4 rounded-2xl border-2 border-dashed border-grey-200 text-grey-400 hover:text-grey-600 hover:border-grey-300 transition-all text-sm font-medium"
            >
              {t('createEvent.addAnotherBoothType')}
            </button>
          )}

          {/* Pro Upsell - Subtle */}
          {!isPro && (
            <div className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-grey-800">{t('createEvent.multipleBoothTypes')}</p>
                  <p className="text-xs text-grey-500 mt-0.5">{t('createEvent.proIncludesTypes')}</p>
                </div>
                <span className="px-3 py-1 text-xs font-medium text-violet-600 bg-white rounded-full shadow-sm">
                  {t('createEvent.comingSoon')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Payment - Clean and Simple */}
        <div className="mb-16">
          <h2 className="text-xs font-medium text-grey-400 uppercase tracking-wider mb-6">
            {t('createEvent.receivePayments')}
          </h2>
          
          {stripeConnected ? (
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-grey-50 border border-grey-200">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                <img src={stripe_logo} alt="Stripe" className="h-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-grey-900">{t('createEvent.stripeConnected')}</p>
                <p className="text-sm text-grey-500">{t('createEvent.stripePaymentsDirect')}</p>
              </div>
              <div className="w-8 h-8 bg-grey-900 rounded-full flex items-center justify-center">
                <CheckIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          ) : (
            <div className="p-6 rounded-2xl bg-grey-25 border border-grey-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-grey-100">
                  <img src={stripe_logo} alt="Stripe" className="h-5 opacity-60" />
                </div>
                <div>
                  <p className="font-medium text-grey-900">{t('createEvent.connectStripe')}</p>
                  <p className="text-sm text-grey-400">{t('createEvent.stripeReceiveDesc')}</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleConnectStripe}
                  className="flex-1 py-3.5 text-sm font-semibold text-white bg-grey-900 hover:bg-grey-800 rounded-xl transition-colors"
                >
                  {t('createEvent.connectStripe')}
                </button>
                <button
                  onClick={handleSaveContinue}
                  className="px-6 py-3.5 text-sm font-medium text-grey-500 hover:text-grey-700 bg-white border border-grey-200 hover:border-grey-300 rounded-xl transition-colors"
                >
                  {t('createEvent.skipForNow')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Fee Disclosure - Ultra Minimal */}
        <div className="mb-12 text-center">
          <p className="text-xs text-grey-400">
            {t('createEvent.platformFeeDisclosure')}
          </p>
        </div>

        {/* Bottom Actions - Clean */}
        <div className="flex items-center justify-between pt-6 border-t border-grey-100">
          <button
            onClick={handleSaveForLaterClick}
            className="text-sm font-medium text-grey-400 hover:text-grey-600 transition-colors"
          >
            {t('createEvent.saveForLaterLink')}
          </button>
          <button
            onClick={handleSaveContinue}
            disabled={!areAllFeesSet}
            className={`px-8 py-3 text-sm font-semibold rounded-full transition-all ${
              areAllFeesSet
                ? "bg-grey-900 text-white hover:bg-grey-800 shadow-lg shadow-grey-900/20"
                : "bg-grey-100 text-grey-300"
            }`}
          >
            {t('createEvent.continue')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CreateEventContent3;
